import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator/loading-indicator';
import { getCurrentOrgId, isModernFeatureManagementEnabled, patchConfigInfo, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BasePageContext } from "../../../contexts/basepage-context";
import { Admin__FeatureManagementScope, useSetFeatureManagementValueMutation, } from '../../../services/feature-management/feature-management-service';
import { getViewMode } from '../../settings/settings.container.util';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { useTscliGroup, } from '../application-settings/hooks/use-tscli-group';
import { ResetConfirmModal } from '../application-settings/reset-confirm-modal';
import { ApplicationSettingsTranslationKeys as Keys } from '../constants';
import { FeatureAccessTab } from '../feature-management/feature-management.util';
import { OverrideOption, PreserveOverrideModal, } from '../feature-management/preserve-override-modal/preserve-override-modal';
import styles from './search-spotiq.module.scss';
import { SearchSpotIQTab } from './search-spotiq-tab';
const SEARCH_GROUP = 'search';
export const SearchSpotIQContainer = ({ currentOrgId, }) => {
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const resolvedOrgId = currentOrgId ?? getCurrentOrgId();
    const viewMode = getViewMode(resolvedOrgId);
    const [toast, setToast] = useState({
        isVisible: false,
        message: '',
        isError: false,
    });
    const [resetModal, setResetModal] = useState({
        isOpen: false,
        configKeys: [],
    });
    const [preserveModal, setPreserveModal] = useState({
        isOpen: false,
        configName: '',
        configLabel: '',
        configValue: '',
        previousValue: '',
        onDismiss: () => undefined,
    });
    const search = useTscliGroup(SEARCH_GROUP);
    const fetchedForViewMode = useRef(null);
    useEffect(() => {
        if (fetchedForViewMode.current === viewMode)
            return;
        fetchedForViewMode.current = viewMode;
        search.fetch(viewMode);
    }, [viewMode, search.fetch]);
    const { setFeatureManagementValue, } = useSetFeatureManagementValueMutation();
    const showSuccess = useCallback((message) => {
        setToast({ isVisible: true, message, isError: false });
    }, []);
    const showError = useCallback((message, retryFn) => {
        setToast({ isVisible: true, message, isError: true, retryFn });
    }, []);
    const retryFn = useCallback(() => {
        if (search.lastError)
            search.retry();
    }, [search.lastError, search.retry]);
    const handleSave = useCallback((rawOptions, vMode) => {
        search.save(rawOptions, vMode ?? viewMode, {
            onMutationSuccess: () => {
                rawOptions.forEach(opt => patchConfigInfo(opt.optionKey, opt.optionValue));
            },
            onSuccess: () => showSuccess(t(Keys.SAVE_SUCCESS)),
            onError: () => showError(t(Keys.SAVE_ERROR), retryFn),
        });
    }, [search, viewMode, showSuccess, showError, retryFn, t]);
    const handleReset = useCallback((configKeys) => {
        setResetModal({ isOpen: true, configKeys });
    }, []);
    const confirmReset = useCallback(() => {
        const { configKeys } = resetModal;
        setResetModal({ isOpen: false, configKeys: [] });
        metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_SETTINGS_RESET), {
            configKeys,
            featureCategory: FeatureAccessTab.GeneralAccess,
            source: 'search-spotiq',
        });
        search.reset(configKeys, viewMode, {
            onSuccess: refreshed => {
                if (refreshed) {
                    const keySet = new Set(configKeys);
                    (refreshed.settings ?? []).forEach(setting => (setting?.configs ?? []).forEach(config => {
                        if (config?.configName &&
                            keySet.has(config.configName) &&
                            config.value !== undefined) {
                            patchConfigInfo(config.configName, config.value);
                        }
                    }));
                }
                showSuccess(t(Keys.RESET_SUCCESS));
            },
            onError: () => showError(t(Keys.RESET_ERROR), retryFn),
        });
    }, [
        resetModal,
        search,
        viewMode,
        showSuccess,
        showError,
        retryFn,
        t,
        metricsService,
    ]);
    const dismissResetModal = useCallback(() => {
        metricsService?.collectEvent(new UserAction(userActionTypeEnum.SETTING_RESET_MODAL_CANCEL), {
            configKeys: resetModal.configKeys,
            featureCategory: FeatureAccessTab.GeneralAccess,
            source: 'search-spotiq',
        });
        setResetModal({ isOpen: false, configKeys: [] });
    }, [metricsService, resetModal]);
    const handlePreserveOverrideNeeded = useCallback((configName, configValue, configLabel, previousValue, onDismiss) => {
        setPreserveModal({
            isOpen: true,
            configName,
            configLabel,
            configValue,
            previousValue,
            onDismiss,
        });
    }, []);
    const handlePreserveUpdate = useCallback((option) => {
        setPreserveModal(prev => ({ ...prev, isOpen: false }));
        setFeatureManagementValue({
            feature_identifier: preserveModal.configName,
            feature_value: preserveModal.configValue,
            scope: Admin__FeatureManagementScope.Cluster,
            reset_org_overrides: option === OverrideOption.IGNORE,
        }, () => {
            patchConfigInfo(preserveModal.configName, preserveModal.configValue);
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_FEATURE_OVERRIDE), {
                featureId: preserveModal.configName,
                featureName: preserveModal.configLabel,
                previousValue: preserveModal.previousValue,
                newValue: preserveModal.configValue,
                featureCategory: FeatureAccessTab.GeneralAccess,
                source: 'search-spotiq',
            });
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.FEATURE_OVERRIDE_PRESERVED), {
                featureId: preserveModal.configName,
                featureName: preserveModal.configLabel,
                selectedOption: option,
            });
            showSuccess(t(Keys.SAVE_SUCCESS));
            search.fetch(viewMode);
        }, () => {
            preserveModal.onDismiss();
            showError(t(Keys.SAVE_ERROR), () => handlePreserveUpdate(option));
        });
    }, [
        preserveModal,
        metricsService,
        setFeatureManagementValue,
        showSuccess,
        showError,
        search,
        viewMode,
        t,
    ]);
    const handleSaveOrgAware = useCallback((configName, configValue, previousValue, onError) => {
        const orgId = getCurrentOrgId();
        setFeatureManagementValue({
            feature_identifier: configName,
            feature_value: configValue,
            scope: Admin__FeatureManagementScope.Org,
            org_identifier: orgId,
        }, () => {
            patchConfigInfo(configName, configValue);
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.ORG_FEATURE_OVERRIDE), {
                configName,
                previousValue,
                newValue: configValue,
                orgId,
                featureCategory: FeatureAccessTab.GeneralAccess,
                source: 'search-spotiq',
            });
            showSuccess(t(Keys.SAVE_SUCCESS));
        }, () => {
            onError();
            showError(t(Keys.SAVE_ERROR), () => handleSaveOrgAware(configName, configValue, previousValue, onError));
        });
    }, [metricsService, setFeatureManagementValue, showSuccess, showError, t]);
    const isModernFMEnabled = isModernFeatureManagementEnabled();
    return (React.createElement(React.Fragment, null,
        React.createElement(LoadingIndicator.Global, { hideLoader: !search.isFetching, className: styles.loader }, !search.isFetching && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.header },
                React.createElement(AdminPageHeader, { title: t('tscli.config.search.title') })),
            React.createElement("div", { className: styles.tabContent },
                React.createElement(SearchSpotIQTab, { group: search.groupConfig, isLoading: search.isBackgroundFetching ||
                        search.isSaving, viewMode: viewMode, isModernFMEnabled: isModernFMEnabled, onSave: handleSave, onSaveOrgAware: handleSaveOrgAware, onReset: handleReset, onPreserveOverrideNeeded: handlePreserveOverrideNeeded }))))),
        React.createElement(ResetConfirmModal, { isOpen: resetModal.isOpen, onConfirm: confirmReset, onDismiss: dismissResetModal }),
        React.createElement(PreserveOverrideModal, { isOpen: preserveModal.isOpen, configName: preserveModal.configName, configLabel: preserveModal.configLabel, onDismiss: () => {
                preserveModal.onDismiss();
                setPreserveModal(prev => ({ ...prev, isOpen: false }));
            }, onUpdate: handlePreserveUpdate }),
        React.createElement(GlobalAlert.Toast, { message: toast.message, isVisible: toast.isVisible, onClose: () => setToast(prev => ({ ...prev, isVisible: false })), autoHide: !toast.isError, showCheckmarkIcon: !toast.isError, primaryAction: toast.isError && toast.retryFn
                ? {
                    label: t(Keys.RETRY),
                    onClick: toast.retryFn,
                }
                : undefined })));
};
//# sourceMappingURL=search-spotiq.container.js.map